/* iter-p0-security · Admin Security Console.
 *
 * One panel, five tabs — every abuse-prevention surface in one place:
 *   • Suspended Users
 *   • Content Reports (from the public /reports/content endpoint)
 *   • Abuse@ Mailbox (inbound abuse@crownnrose.com mail)
 *   • URL-scan Strikes (Safe Browsing hits with auto-suspend trail)
 *   • Appeals (suspended users requesting review)
 *
 * All lists refresh on tab change + a single Refresh button. Every
 * action fires a POST/PATCH then re-fetches the affected list so the
 * UI stays consistent without polling.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { PanelHeader } from './_shared';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ShieldAlert, Ban, Flag, Mail, Link2, Inbox, Loader2, CheckCircle2, XCircle,
  Lock, Activity, ShieldX,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, Tooltip as RTooltip,
} from 'recharts';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax = axios.create({ baseURL: API, withCredentials: true });

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

function TrendCard({ label, data, tone, testId }) {
  const total = (data || []).reduce((s, d) => s + (d.count || 0), 0);
  const last = data?.[data.length - 1]?.count || 0;
  const prev = data?.[data.length - 2]?.count || 0;
  const delta = last - prev;
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-mono-stat text-xl font-semibold">{total}</span>
        {delta !== 0 && (
          <span className={`text-[11px] ${delta > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            {delta > 0 ? '↑' : '↓'}{Math.abs(delta)} today
          </span>
        )}
      </div>
      <div className="mt-1 h-8">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
            <Line type="monotone" dataKey="count" stroke={tone} strokeWidth={1.5} dot={false} />
            <RTooltip
              contentStyle={{ fontSize: 11, padding: 6 }}
              labelStyle={{ fontSize: 10, color: '#666' }}
              formatter={(v) => [v, label]}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ConfirmationsList({ rows, onConfirm }) {
  if (!rows?.length) return <Empty label="No pending permanent-suspend confirmations." />;
  return (
    <div className="space-y-2" data-testid="sec-confirmations-list">
      {rows.map((c) => (
        <div key={c.id}
             className="rounded-md border border-amber-300 bg-amber-50/50 p-3 text-[12.5px] dark:bg-amber-950/20">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Lock className="h-3.5 w-3.5 text-amber-800" />
                <span className="font-mono text-[11px]">{c.user_id}</span>
                <Badge variant="outline" className="text-[10px]">initiator: {c.initiator}</Badge>
                <span className="text-[11px] text-muted-foreground">{fmtDate(c.created_at)}</span>
              </div>
              <div className="mt-1 whitespace-pre-wrap break-words text-[12px]">{c.reason}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                Expires {fmtDate(c.expires_at)} — if not confirmed by then, falls back to a reversible suspend.
              </div>
            </div>
            <Button size="sm" variant="destructive" onClick={() => onConfirm(c)}
                    data-testid={`sec-confirm-${c.id}`}>
              Confirm permanent
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground">
      {label}
    </div>
  );
}

function ActionDialog({ open, onOpenChange, title, defaultText,
                        options, onConfirm, busy }) {
  const [text, setText] = useState(defaultText || '');
  const [action, setAction] = useState(options?.[0]?.value || 'dismiss');
  useEffect(() => { setText(defaultText || ''); }, [defaultText, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sec-action-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {options?.map((o) => (
              <Button
                key={o.value}
                size="sm"
                variant={action === o.value ? 'default' : 'outline'}
                onClick={() => setAction(o.value)}
                data-testid={`sec-action-${o.value}`}
              >
                {o.label}
              </Button>
            ))}
          </div>
          <Textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Note / resolution message (min 3 chars)"
            className="min-h-[100px] text-[12.5px]"
            data-testid="sec-action-note"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  disabled={busy}>Cancel</Button>
          <Button
            onClick={() => onConfirm({ action, text })}
            disabled={busy || (text || '').trim().length < 3}
            data-testid="sec-action-confirm"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuspendedList({ rows, onUnsuspend }) {
  if (!rows?.length) return <Empty label="No suspended users. 🎉" />;
  return (
    <div className="space-y-2" data-testid="sec-suspended-list">
      {rows.map((u) => (
        <div key={u.user_id}
             className="flex items-start justify-between gap-4 rounded-md border border-border bg-card p-3 text-[12.5px]">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold" data-testid={`sec-suspended-${u.user_id}`}>{u.email || u.user_id}</span>
              <Badge variant="outline" className="text-[10px]">{u.suspended_trigger || 'manual'}</Badge>
              {u.url_scan_strikes ? (
                <Badge variant="destructive" className="text-[10px]">
                  {u.url_scan_strikes} URL strikes
                </Badge>
              ) : null}
            </div>
            <div className="mt-1 text-[11.5px] text-muted-foreground">
              Suspended {fmtDate(u.suspended_at)} by <b>{u.suspended_by}</b>
            </div>
            <div className="mt-1 whitespace-pre-wrap break-words text-[12px] text-foreground/80">
              {u.suspended_reason}
            </div>
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => onUnsuspend(u)}
                  data-testid={`sec-unsuspend-${u.user_id}`}>
            Unsuspend
          </Button>
        </div>
      ))}
    </div>
  );
}

function ReportsList({ rows, onResolve }) {
  if (!rows?.length) return <Empty label="No open reports." />;
  return (
    <div className="space-y-2" data-testid="sec-reports-list">
      {rows.map((r) => (
        <div key={r.id}
             className="rounded-md border border-border bg-card p-3 text-[12.5px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="text-[10px] uppercase">{r.kind}</Badge>
                {r.target_id && (
                  <span className="font-mono text-[11px] text-muted-foreground">
                    target: {r.target_id}
                  </span>
                )}
                <span className="text-[11px] text-muted-foreground">
                  by {r.reporter_email || 'anon'} · {fmtDate(r.created_at)}
                </span>
              </div>
              <div className="mt-1 whitespace-pre-wrap break-words">{r.reason}</div>
            </div>
            {r.status === 'open' && (
              <Button size="sm" onClick={() => onResolve(r)}
                      data-testid={`sec-resolve-${r.id}`}>
                Review
              </Button>
            )}
            {r.status !== 'open' && (
              <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AbuseList({ rows, onResolve }) {
  if (!rows?.length) return <Empty label="Abuse mailbox is quiet." />;
  return (
    <div className="space-y-2" data-testid="sec-abuse-list">
      {rows.map((r) => (
        <div key={r.id}
             className="rounded-md border border-border bg-card p-3 text-[12.5px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold truncate">{r.subject || '(no subject)'}</span>
                <span className="text-[11px] text-muted-foreground">
                  from {r.from_addr} · {fmtDate(r.at)}
                </span>
              </div>
              <div className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded bg-muted/40 p-2 text-[11.5px]">
                {r.body_text || '(empty body)'}
              </div>
            </div>
            {r.status === 'open' ? (
              <Button size="sm" onClick={() => onResolve(r)}
                      data-testid={`sec-abuse-resolve-${r.id}`}>
                Mark resolved
              </Button>
            ) : (
              <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StrikesList({ rows }) {
  if (!rows?.length) return <Empty label="No URL-safety strikes recorded." />;
  return (
    <div className="space-y-2" data-testid="sec-strikes-list">
      {rows.map((s) => (
        <div key={s.id}
             className="rounded-md border border-red-200 bg-red-50/50 p-3 text-[12.5px] dark:bg-red-950/20">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Link2 className="h-3.5 w-3.5 text-red-700" />
                <span className="font-mono text-[11px]">{s.user_id}</span>
                <span className="text-[11px] text-muted-foreground">
                  → {s.to_addr} · {fmtDate(s.at)}
                </span>
              </div>
              <div className="mt-1 text-[11.5px]">
                <span className="text-muted-foreground">subject: </span>
                {s.subject || '(none)'}
              </div>
              <ul className="mt-1 list-disc pl-4 text-[11.5px] text-red-800 dark:text-red-300">
                {(s.matches || []).map((m, i) => (
                  <li key={i} className="truncate font-mono">{m}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AppealsList({ rows, onResolve }) {
  if (!rows?.length) return <Empty label="No open appeals." />;
  return (
    <div className="space-y-2" data-testid="sec-appeals-list">
      {rows.map((a) => (
        <div key={a.id}
             className="rounded-md border border-border bg-card p-3 text-[12.5px]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] text-muted-foreground">{a.user_id}</span>
                <span className="text-[11px]">← {a.email}</span>
                <span className="text-[11px] text-muted-foreground">{fmtDate(a.created_at)}</span>
              </div>
              {a.user_suspend_reason && (
                <div className="mt-1 rounded bg-amber-50 p-2 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  <b>Original suspend reason:</b> {a.user_suspend_reason}
                </div>
              )}
              <div className="mt-1 whitespace-pre-wrap break-words">{a.reason}</div>
              {a.contact_phone && (
                <div className="mt-1 text-[11px] text-muted-foreground">
                  phone: <span className="font-mono">{a.contact_phone}</span>
                </div>
              )}
              {(a.notes || []).length > 0 && (
                <details className="mt-1 text-[11.5px]">
                  <summary className="cursor-pointer text-muted-foreground">
                    {a.notes.length} follow-up note(s)
                  </summary>
                  <div className="mt-1 space-y-1">
                    {a.notes.map((n, i) => (
                      <div key={i} className="rounded bg-muted/30 p-2 text-[11px]">
                        <div className="text-muted-foreground">{fmtDate(n.at)}</div>
                        {n.text}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
            {a.status === 'open' && (
              <Button size="sm" onClick={() => onResolve(a)}
                      data-testid={`sec-appeal-resolve-${a.id}`}>
                Review
              </Button>
            )}
            {a.status !== 'open' && (
              <Badge variant="outline" className="text-[10px]">{a.status}</Badge>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function BlockedIpsList({ rows, onUnban }) {
  if (!rows?.length) return <Empty label="No IPs currently blocked." />;
  return (
    <div className="space-y-2" data-testid="sec-blocked-ips-list">
      {rows.map((b) => (
        <div key={b.ip}
             className="flex items-start justify-between gap-4 rounded-md border border-border bg-card p-3 text-[12.5px]">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-semibold" data-testid={`sec-blocked-ip-${b.ip}`}>{b.ip}</span>
              <Badge variant={b.banned_by === 'auto' ? 'destructive' : 'secondary'} className="text-[10px]">
                {b.banned_by === 'auto' ? 'auto-blocked' : (b.banned_by || 'manual')}
              </Badge>
              {b.hit_count > 0 && (
                <Badge variant="outline" className="text-[10px]">{b.hit_count} hits</Badge>
              )}
              <span className="text-[11px] text-muted-foreground">
                {b.expires_at ? `expires ${fmtDate(b.expires_at)}` : 'permanent'}
              </span>
            </div>
            <div className="mt-1 text-[11.5px] text-muted-foreground">
              Blocked {fmtDate(b.created_at)}
            </div>
            <div className="mt-1 whitespace-pre-wrap break-words text-[12px] text-foreground/80">
              {b.reason}
            </div>
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => onUnban(b)}
                  data-testid={`sec-unban-${b.ip}`}>
            Unban
          </Button>
        </div>
      ))}
    </div>
  );
}

function SuspiciousEventsList({ rows }) {
  if (!rows?.length) return <Empty label="No suspicious probing activity recorded." />;
  return (
    <div className="space-y-1.5" data-testid="sec-events-list">
      {rows.map((e) => (
        <div key={e.id}
             className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-[11.5px]">
          <Activity className="h-3.5 w-3.5 shrink-0 text-amber-700" />
          <span className="font-mono">{e.ip}</span>
          <Badge variant="outline" className="text-[10px]">{e.status_code}</Badge>
          <Badge variant="secondary" className="text-[10px]">{e.reason_tag}</Badge>
          <span className="truncate font-mono text-muted-foreground">{e.path}</span>
          <span className="ml-auto shrink-0 text-muted-foreground">{fmtDate(e.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

function BanIpDialog({ open, onOpenChange, onSubmit, busy }) {
  const [ip, setIp] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('permanent');
  useEffect(() => { if (open) { setIp(''); setReason(''); setDuration('permanent'); } }, [open]);
  const durationHours = duration === 'permanent' ? null : Number(duration);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sec-ban-ip-dialog">
        <DialogHeader><DialogTitle>Block an IP address</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input
            value={ip} onChange={(e) => setIp(e.target.value)}
            placeholder="e.g. 203.0.113.42"
            className="font-mono text-[12.5px]"
            data-testid="sec-ban-ip-input"
          />
          <Textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (min 3 chars)"
            className="min-h-[80px] text-[12.5px]"
            data-testid="sec-ban-ip-reason"
          />
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger data-testid="sec-ban-ip-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="permanent">Permanent</SelectItem>
              <SelectItem value="1">1 hour</SelectItem>
              <SelectItem value="24">24 hours</SelectItem>
              <SelectItem value="168">7 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={() => onSubmit({ ip: ip.trim(), reason: reason.trim(), duration_hours: durationHours })}
            disabled={busy || ip.trim().length < 3 || reason.trim().length < 3}
            data-testid="sec-ban-ip-confirm"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
            Block IP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSecurityPanel() {
  const [tab, setTab] = useState('suspended');
  const [busy, setBusy] = useState(false);
  const [suspended, setSuspended] = useState([]);
  const [reports, setReports] = useState([]);
  const [abuse, setAbuse] = useState([]);
  const [strikes, setStrikes] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [events, setEvents] = useState([]);
  const [timeseries, setTimeseries] = useState(null);
  const [dialogState, setDialogState] = useState({ open: false, item: null, kind: null });
  const [banDialogOpen, setBanDialogOpen] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [s, r, a, k, ap, cf, ts, bi, ev] = await Promise.all([
        ax.get('/admin/users/suspended?limit=100').then((x) => x.data?.users || []).catch(() => []),
        ax.get('/admin/reports?status=open&limit=100').then((x) => x.data?.reports || []).catch(() => []),
        ax.get('/admin/abuse-reports?status=open&limit=100').then((x) => x.data?.reports || []).catch(() => []),
        ax.get('/admin/url-scan/strikes?limit=50').then((x) => x.data?.strikes || []).catch(() => []),
        ax.get('/admin/appeals?status=open&limit=100').then((x) => x.data?.appeals || []).catch(() => []),
        ax.get('/admin/suspend-confirmations?status=pending&limit=50').then((x) => x.data?.confirmations || []).catch(() => []),
        ax.get('/admin/security/timeseries?days=14').then((x) => x.data).catch(() => null),
        ax.get('/admin/security/blocked-ips?limit=200').then((x) => x.data?.blocked_ips || []).catch(() => []),
        ax.get('/admin/security/suspicious-events?limit=100').then((x) => x.data?.events || []).catch(() => []),
      ]);
      setSuspended(s); setReports(r); setAbuse(a); setStrikes(k); setAppeals(ap);
      setConfirmations(cf); setTimeseries(ts); setBlockedIps(bi); setEvents(ev);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openDialog = (kind, item) => setDialogState({ open: true, kind, item });
  const closeDialog = () => setDialogState({ open: false, kind: null, item: null });

  const onUnban = async (b) => {
    if (!window.confirm(`Unban ${b.ip}?`)) return;
    setBusy(true);
    try {
      await ax.delete(`/admin/security/blocked-ips/${encodeURIComponent(b.ip)}`);
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onBanIp = async ({ ip, reason, duration_hours }) => {
    setBusy(true);
    try {
      await ax.post('/admin/security/blocked-ips', { ip, reason, duration_hours });
      setBanDialogOpen(false);
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async ({ action, text }) => {
    const { kind, item } = dialogState;
    if (!item) return;
    setBusy(true);
    try {
      if (kind === 'unsuspend') {
        await ax.post(`/admin/users/${item.user_id}/unsuspend`, { reason: text });
      } else if (kind === 'report') {
        await ax.patch(`/admin/reports/${item.id}`, { resolution: text, action });
      } else if (kind === 'abuse') {
        await ax.patch(`/admin/abuse-reports/${item.id}`, { note: text });
      } else if (kind === 'appeal') {
        await ax.patch(`/admin/appeals/${item.id}`, { resolution: text, action });
      } else if (kind === 'confirm-permanent') {
        await ax.post(`/admin/suspend-confirmations/${item.id}/confirm`, {});
      }
      closeDialog();
      await refresh();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const dialogCfg = useMemo(() => {
    const { kind } = dialogState;
    if (kind === 'unsuspend') return {
      title: 'Unsuspend user',
      options: [{ value: 'dismiss', label: 'Restore access' }],
    };
    if (kind === 'report') return {
      title: 'Resolve content report',
      options: [
        { value: 'dismiss',  label: 'Dismiss (no action)' },
        { value: 'resolve',  label: 'Resolve (took action)' },
        { value: 'suspend',  label: 'Suspend user' },
      ],
    };
    if (kind === 'abuse') return {
      title: 'Resolve abuse@ report',
      options: [{ value: 'dismiss', label: 'Mark resolved' }],
    };
    if (kind === 'appeal') return {
      title: 'Resolve appeal',
      options: [
        { value: 'dismiss',   label: 'Keep suspended' },
        { value: 'unsuspend', label: 'Approve — restore account' },
      ],
    };
    if (kind === 'confirm-permanent') return {
      title: 'Confirm permanent suspension',
      options: [],
    };
    return { title: '', options: [] };
  }, [dialogState]);

  return (
    <div className="space-y-4" data-testid="admin-security-panel">
      <PanelHeader
        title="Security Console"
        subtitle="Every abuse-prevention surface in one place — suspended users, public reports, abuse@ mailbox, URL-safety strikes, and account appeals."
        onRefresh={refresh}
      />

      {timeseries && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="sec-trend-cards">
          <TrendCard label="Reports · 14d" data={timeseries.reports}  tone="#c2410c" testId="sec-trend-reports" />
          <TrendCard label="Abuse@ · 14d"  data={timeseries.abuse}    tone="#a16207" testId="sec-trend-abuse" />
          <TrendCard label="URL Strikes · 14d" data={timeseries.strikes} tone="#b91c1c" testId="sec-trend-strikes" />
          <TrendCard label="Suspends · 14d" data={timeseries.suspends} tone="#7c1c1c" testId="sec-trend-suspends" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="suspended" className="gap-1.5" data-testid="sec-tab-suspended">
            <Ban className="h-3.5 w-3.5" /> Suspended
            {suspended.length > 0 && <Badge variant="destructive" className="text-[10px]">{suspended.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="confirmations" className="gap-1.5" data-testid="sec-tab-confirmations">
            <Lock className="h-3.5 w-3.5" /> Confirm
            {confirmations.length > 0 && <Badge variant="destructive" className="text-[10px]">{confirmations.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5" data-testid="sec-tab-reports">
            <Flag className="h-3.5 w-3.5" /> Reports
            {reports.length > 0 && <Badge variant="secondary" className="text-[10px]">{reports.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="abuse" className="gap-1.5" data-testid="sec-tab-abuse">
            <Inbox className="h-3.5 w-3.5" /> Abuse@
            {abuse.length > 0 && <Badge variant="secondary" className="text-[10px]">{abuse.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="strikes" className="gap-1.5" data-testid="sec-tab-strikes">
            <Link2 className="h-3.5 w-3.5" /> URL Strikes
          </TabsTrigger>
          <TabsTrigger value="appeals" className="gap-1.5" data-testid="sec-tab-appeals">
            <Mail className="h-3.5 w-3.5" /> Appeals
            {appeals.length > 0 && <Badge variant="secondary" className="text-[10px]">{appeals.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="blocked-ips" className="gap-1.5" data-testid="sec-tab-blocked-ips">
            <ShieldX className="h-3.5 w-3.5" /> Blocked IPs
            {blockedIps.length > 0 && <Badge variant="destructive" className="text-[10px]">{blockedIps.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="events" className="gap-1.5" data-testid="sec-tab-events">
            <Activity className="h-3.5 w-3.5" /> Suspicious Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="suspended" className="mt-3">
          <SuspendedList rows={suspended} onUnsuspend={(u) => openDialog('unsuspend', u)} />
        </TabsContent>
        <TabsContent value="confirmations" className="mt-3">
          <ConfirmationsList rows={confirmations}
                              onConfirm={(c) => openDialog('confirm-permanent', c)} />
        </TabsContent>
        <TabsContent value="reports" className="mt-3">
          <ReportsList rows={reports} onResolve={(r) => openDialog('report', r)} />
        </TabsContent>
        <TabsContent value="abuse" className="mt-3">
          <AbuseList rows={abuse} onResolve={(r) => openDialog('abuse', r)} />
        </TabsContent>
        <TabsContent value="strikes" className="mt-3">
          <StrikesList rows={strikes} />
        </TabsContent>
        <TabsContent value="appeals" className="mt-3">
          <AppealsList rows={appeals} onResolve={(a) => openDialog('appeal', a)} />
        </TabsContent>
        <TabsContent value="blocked-ips" className="mt-3 space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setBanDialogOpen(true)} data-testid="sec-open-ban-ip-dialog">
              <ShieldX className="h-3.5 w-3.5 mr-1.5" /> Block an IP
            </Button>
          </div>
          <BlockedIpsList rows={blockedIps} onUnban={onUnban} />
        </TabsContent>
        <TabsContent value="events" className="mt-3">
          <SuspiciousEventsList rows={events} />
        </TabsContent>
      </Tabs>

      <BanIpDialog
        open={banDialogOpen}
        onOpenChange={setBanDialogOpen}
        onSubmit={onBanIp}
        busy={busy}
      />

      <ActionDialog
        open={dialogState.open}
        onOpenChange={(v) => (!v ? closeDialog() : null)}
        title={dialogCfg.title}
        options={dialogCfg.options}
        onConfirm={onConfirm}
        busy={busy}
      />
    </div>
  );
}
